// components/WaterWatchFeed.jsx
// =============================================================================
// WATER WATCH v2, the live local water intelligence feed inside WTR INTEL.
//
// Sections, top to bottom (mirrors the Water Quality Watch prototype):
//   1. Header: watching chip, monitoring line, Enable Alerts opt in.
//   2. ALERTS: critical items (boil water, do not drink) detected in local
//      reporting. Red banner, action guidance, always linked to the source.
//   3. ACTIVE FINDINGS: verified tier, built from the SAME report contaminants
//      already on screen, so Report and Watch can never disagree.
//   4. TRENDING NEAR YOU: Reddit community discussion, developing tier.
//   5. WATCH AND LEARN: educational videos picked for this water profile via
//      the YouTube Data API (real thumbnails from real API responses).
//   6. LOCAL REPORTING: press coverage, developing tier.
// Every state is honest: real items, explicit empty states with last checked
// time, or a visible error. Nothing fabricated.
// =============================================================================

import { useState, useEffect } from "react";

const API_BASE = "https://generosity-sales-engine-mvp-api.onrender.com";
import { SERVICE_TOKEN } from "../lib/apiConfig";
const ALERTS_KEY = "wtr_watch_alerts_on";

const C = {
  navy: "#0F1419", text: "#3D4043", muted: "#A6A8AB",
  blue: "#51B0E6", blueDark: "#1F6FA0",
  green: "#4A8A6F", red: "#B84A4A", amber: "#C89B3C",
};

const ALERT_GUIDANCE = {
  boil_water: [
    "Boil water for at least 1 minute before drinking or cooking.",
    "Use bottled or boiled water for infants and immunocompromised individuals.",
    "Avoid ice makers and filters connected to the tap until cleared.",
  ],
  do_not_drink: [
    "Do not drink or cook with tap water until the advisory is lifted.",
    "Boiling may NOT make the water safe for this advisory type.",
    "Use bottled water for drinking, cooking, and brushing teeth.",
  ],
  advisory: [
    "Read the linked advisory for the affected area and instructions.",
    "Sensitive individuals should consider bottled or filtered water.",
  ],
};

function relTime(d) {
  if (!d) return null;
  const ms = Date.now() - new Date(d).getTime();
  if (isNaN(ms)) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function TierChip({ tier }) {
  const map = {
    verified:   { label: "VERIFIED",   color: C.green },
    developing: { label: "DEVELOPING", color: C.amber },
    critical:   { label: "CRITICAL",   color: C.red },
  };
  const t = map[tier] || map.developing;
  return (
    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1, color: t.color, background: `${t.color}14`, border: `1px solid ${t.color}30`, borderRadius: 10, padding: "2px 7px", textTransform: "uppercase", whiteSpace: "nowrap", flexShrink: 0 }}>
      {t.label}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-micro uppercase tracking-widest font-semibold" style={{ fontSize: 10, color: C.muted, margin: "16px 0 8px" }}>
      {children}
    </div>
  );
}

function WatchCard({ icon, tier, title, body, meta, accent, onTap }) {
  return (
    <div className="card-default" onClick={onTap || undefined}
      style={{ padding: "13px 14px", marginBottom: 10, cursor: onTap ? "pointer" : "default", borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0, background: `${accent}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.navy, lineHeight: 1.35 }}>{title}</span>
            <TierChip tier={tier} />
          </div>
          {body && <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{body}</div>}
          {meta && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 5, fontWeight: 500 }}>{meta}</div>}
        </div>
      </div>
    </div>
  );
}

const HazardIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2L22 20H2L12 2Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/><line x1="12" y1="9" x2="12" y2="14" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><circle cx="12" cy="17" r="1" fill={color}/></svg>
);
const NewsIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="16" rx="2" stroke={color} strokeWidth="1.6"/><line x1="7" y1="9" x2="17" y2="9" stroke={color} strokeWidth="1.4" strokeLinecap="round"/><line x1="7" y1="13" x2="17" y2="13" stroke={color} strokeWidth="1.4" strokeLinecap="round"/><line x1="7" y1="17" x2="13" y2="17" stroke={color} strokeWidth="1.4" strokeLinecap="round"/></svg>
);
const EyeIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M2 12C4.5 7 8 4.5 12 4.5C16 4.5 19.5 7 22 12C19.5 17 16 19.5 12 19.5C8 19.5 4.5 17 2 12Z" stroke={color} strokeWidth="1.6"/><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.6"/></svg>
);
const TrendIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 17 9 11 13 15 21 7" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><polyline points="15 7 21 7 21 13" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
const BellIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 9A6 6 0 0 0 6 9C6 14 4 16 4 16H20C20 16 18 14 18 9Z" stroke={color} strokeWidth="1.7" strokeLinejoin="round"/><path d="M10 20A2 2 0 0 0 14 20" stroke={color} strokeWidth="1.7" strokeLinecap="round"/></svg>
);

export default function WaterWatchFeed({ zip, pwsid, utility, city, contaminants = [], hasReport = false, onScan, onEnableAlerts }) {
  const [feed, setFeed] = useState(null); // null loading, object loaded
  const [feedError, setFeedError] = useState(false);
  const [alertsOn, setAlertsOn] = useState(() => {
    try { return localStorage.getItem(ALERTS_KEY) === "true"; } catch (e) { return false; }
  });

  const areaLabel = utility || city || (zip ? `ZIP ${zip}` : "your area");

  useEffect(() => {
    if (!hasReport || (!pwsid && !zip)) return;
    let cancelled = false;
    setFeed(null); setFeedError(false);
    const params = new URLSearchParams();
    if (pwsid) params.set("pwsid", pwsid);
    if (zip) params.set("zip", zip);
    if (utility) params.set("utility", utility);
    if (city) params.set("city", city);
    const topics = contaminants
      .filter(c => c.risk === "high")
      .map(c => (c.name || "").split("(")[0].trim())
      .filter(Boolean).slice(0, 3).join(",");
    if (topics) params.set("topics", topics);
    fetch(`${API_BASE}/api/wtr-watch/feed?${params.toString()}`, {
      headers: { Authorization: "Bearer " + SERVICE_TOKEN },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) setFeed(j); })
      .catch(() => { if (!cancelled) { setFeed({ alerts: [], news: [], social: [], videos: [] }); setFeedError(true); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [hasReport, pwsid, zip, utility, city]);

  function enableAlerts() {
    try { localStorage.setItem(ALERTS_KEY, "true"); } catch (e) {}
    setAlertsOn(true);
    if (onEnableAlerts) onEnableAlerts();
  }

  if (!hasReport) {
    return (
      <div style={{ textAlign: "center", padding: "50px 20px" }} data-testid="watch-empty">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><EyeIcon color={C.muted} /></div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, marginBottom: 6 }}>Water Quality Watch</div>
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, maxWidth: 300, margin: "0 auto 16px" }}>
          Run a water scan first. Water Quality Watch then monitors your utility for emergencies, violations, community discussion, and local reporting.
        </div>
        <button type="button" onClick={onScan} className="btn-brand font-semibold rounded-card cursor-pointer" style={{ padding: "12px 22px", fontSize: 13, border: "none" }}>
          TEST MY WATER {"\u2192"}
        </button>
      </div>
    );
  }

  const violations = contaminants.filter(c => c.isViolation);
  const exceedances = contaminants.filter(c => !c.isViolation && (c.exceedsMcl || (c.risk === "high" && c.exceedsEwg)));
  const verifiedItems = [
    ...violations.slice(0, 3).map(c => ({
      key: `vio-${c.name}`, icon: <HazardIcon color={C.red} />, accent: C.red, tier: "verified",
      title: `${c.name}: formal violation on record`,
      body: c.detail || `${areaLabel} has a formal SDWIS violation for ${c.name}.`,
      meta: `EPA SDWIS \u00B7 ${areaLabel}`,
    })),
    ...exceedances.slice(0, 3).map(c => ({
      key: `exc-${c.name}`, icon: <HazardIcon color={C.amber} />, accent: C.amber, tier: "verified",
      title: `${c.name} above health guideline`,
      body: c.detail || `${c.name} was detected above its health guideline in ${areaLabel}.`,
      meta: `${c.level != null ? `${c.level} ${c.unit || ""} \u00B7 ` : ""}${areaLabel}`,
    })),
  ].slice(0, 4);

  const alerts = feed?.alerts || [];
  const news = feed?.news || [];
  const social = feed?.social || [];
  const videos = feed?.videos || [];
  const loading = feed === null;

  return (
    <div data-testid="watch-feed">
      {/* 1. Header */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>Water Quality Watch</div>
          <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(81,176,230,0.08)", border: "1px solid rgba(81,176,230,0.20)", borderRadius: 20, padding: "3px 9px" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: C.blueDark }}>Watching {areaLabel}</span>
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
          Monitoring water quality for {areaLabel}{zip ? ` (ZIP ${zip})` : ""}. Emergencies, findings, community discussion, and local reporting in one place.
        </div>
      </div>

      {/* Enable Alerts */}
      <div style={{ margin: "10px 0 4px" }}>
        {alertsOn ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(74,138,111,0.08)", border: "1px solid rgba(74,138,111,0.22)", borderRadius: 12, padding: "7px 12px" }}>
            <BellIcon color={C.green} />
            <span style={{ fontSize: 11.5, fontWeight: 700, color: C.green }}>Alerts on for {zip ? `ZIP ${zip}` : areaLabel}</span>
          </div>
        ) : (
          <button type="button" onClick={enableAlerts} className="btn-brand rounded-card cursor-pointer" style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", fontSize: 12, fontWeight: 700, border: "none" }}>
            <BellIcon color="#FFFFFF" /> Enable Alerts
          </button>
        )}
      </div>

      {/* 2. ALERTS */}
      {alerts.length > 0 && (
        <>
          <SectionLabel>ACTIVE ALERTS</SectionLabel>
          {alerts.map((a, i) => (
            <div key={`al-${i}`} className="card-critical" style={{ padding: "14px 15px", marginBottom: 10, cursor: "pointer" }}
              onClick={() => { try { window.open(a.source_url, "_blank", "noopener"); } catch (e) {} }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <BellIcon color={C.red} />
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: C.red }}>
                    {a.kind === "boil_water" ? "Boil-Water Alert" : a.kind === "do_not_drink" ? "Do-Not-Drink Alert" : "Water Advisory"}
                  </span>
                </div>
                <TierChip tier="critical" />
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: C.navy, lineHeight: 1.45, marginBottom: 8 }}>{a.title}</div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {(ALERT_GUIDANCE[a.kind] || ALERT_GUIDANCE.advisory).map((g, gi) => (
                  <li key={gi} style={{ fontSize: 11.5, color: "#7A2A2A", lineHeight: 1.6 }}>{g}</li>
                ))}
              </ul>
              <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8, fontWeight: 500 }}>
                Detected in local reporting{a.source_name ? ` (${a.source_name})` : ""}{relTime(a.first_seen) ? ` \u00B7 ${relTime(a.first_seen)}` : ""} \u00B7 Tap to read the source
              </div>
            </div>
          ))}
        </>
      )}

      {/* 3. ACTIVE FINDINGS */}
      <SectionLabel>ACTIVE FINDINGS</SectionLabel>
      {verifiedItems.length > 0 ? verifiedItems.map(it => (
        <WatchCard key={it.key} icon={it.icon} accent={it.accent} tier={it.tier} title={it.title} body={it.body} meta={it.meta} />
      )) : (
        <div className="card-default" style={{ padding: "14px 16px", marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <EyeIcon color={C.green} />
          <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
            No active violations or guideline exceedances in your report for {areaLabel}. Water Quality Watch keeps monitoring.
          </div>
        </div>
      )}

      {/* 4. TRENDING */}
      <SectionLabel>TRENDING NEAR YOU</SectionLabel>
      {loading && (
        <div className="card-default" style={{ padding: 16, marginBottom: 10, textAlign: "center" }}>
          <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: "50%", border: "2px solid #51B0E6", borderTopColor: "transparent", animation: "spin 1s linear infinite", verticalAlign: "middle" }} />
          <span style={{ fontSize: 12, color: C.muted, marginLeft: 8 }}>Checking community discussion{"\u2026"}</span>
        </div>
      )}
      {!loading && social.length > 0 && social.map((s, i) => (
        <WatchCard
          key={`soc-${i}`} icon={<TrendIcon color={C.blueDark} />} accent={C.blueDark} tier="developing"
          title={s.title} body={null}
          meta={`${s.community || "Reddit"} \u00B7 \u25B2 ${s.score} \u00B7 ${s.comments} comments${relTime(s.published_at) ? ` \u00B7 ${relTime(s.published_at)}` : ""} \u00B7 Tap to read`}
          onTap={() => { try { window.open(s.source_url, "_blank", "noopener"); } catch (e) {} }}
        />
      ))}
      {!loading && social.length === 0 && (
        <div className="card-default" style={{ padding: "13px 16px", marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
            No trending community discussion about {areaLabel} water this month.
          </div>
        </div>
      )}

      {/* 5. WATCH AND LEARN */}
      {(loading || videos.length > 0) && (
        <>
          <SectionLabel>WATCH AND LEARN</SectionLabel>
          {loading ? (
            <div className="card-default" style={{ padding: 16, marginBottom: 10, textAlign: "center" }}>
              <span style={{ fontSize: 12, color: C.muted }}>Loading videos for your water profile{"\u2026"}</span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6, marginBottom: 6, WebkitOverflowScrolling: "touch" }}>
              {videos.map((v, i) => (
                <div key={`vid-${i}`} className="card-default" style={{ width: 200, flexShrink: 0, padding: 0, overflow: "hidden", cursor: "pointer" }}
                  onClick={() => { try { window.open(`https://www.youtube.com/watch?v=${v.video_id}`, "_blank", "noopener"); } catch (e) {} }}>
                  <div style={{ position: "relative", width: "100%", height: 112, background: "#F0F1F3" }}>
                    {v.thumb_url && <img src={v.thumb_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(15,20,25,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFFFFF"><polygon points="7,4 21,12 7,20"/></svg>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: "9px 11px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: C.navy, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{v.title}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 4, fontWeight: 500 }}>{v.channel || "YouTube"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && videos.length > 0 && (
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Picked for your water profile. Opens in YouTube.</div>
          )}
        </>
      )}

      {/* 6. LOCAL REPORTING */}
      <SectionLabel>LOCAL REPORTING</SectionLabel>
      {loading && (
        <div className="card-default" style={{ padding: 16, marginBottom: 10, textAlign: "center" }}>
          <span style={{ fontSize: 12, color: C.muted }}>Checking local coverage{"\u2026"}</span>
        </div>
      )}
      {!loading && news.length > 0 && news.map((n, i) => (
        <WatchCard
          key={`news-${i}`} icon={<NewsIcon color={C.blue} />} accent={C.blue} tier="developing"
          title={n.title} body={null}
          meta={`Reported by ${n.source_name || "local press"}${relTime(n.published_at) ? ` \u00B7 ${relTime(n.published_at)}` : ""} \u00B7 Tap to read`}
          onTap={() => { try { window.open(n.source_url, "_blank", "noopener"); } catch (e) {} }}
        />
      ))}
      {!loading && news.length === 0 && (
        <div className="card-default" style={{ padding: "14px 16px", marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
            {feedError
              ? "Could not reach the Water Quality Watch service. Findings above are from your report and are unaffected."
              : `No recent local coverage found for ${areaLabel}. That is usually good news.`}
          </div>
        </div>
      )}

      {/* Provenance footer */}
      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6, marginTop: 8, padding: "0 2px" }}>
        Verified tier: EPA SDWIS, UCMR5, and state detection data. Developing tier: third party reporting, community discussion, and educational video, attributed and linked, never restated as our own claims.
        {feed?.last_checked && relTime(feed.last_checked) ? ` Sources last checked ${relTime(feed.last_checked)}.` : ""}
      </div>
    </div>
  );
}
